import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import cloneDeep from 'lodash/cloneDeep';
import slice from 'lodash/slice';
import merge from 'lodash/merge';
import xor from 'lodash/xor';
import { getFormBuilderApiClient } from 'lib/SplashApiClient/subclients/FormBuilderApiClient';
import { getEventId } from 'common/store/events/selectors';

import { denormalize } from 'normalizr';

import uuid from 'utils/id';

import { notifyError, notifySuccess } from 'common/compounds/Notification/modules';

import { REGISTRATION_METHOD_RSVP, REGISTRATION_METHOD_TICKETS } from 'common/constants/registrationMethods';
import {
    ENTITIES_KEY_FORM,
    serializeForm,
    addRawFormData,
    normalizeForm,
    FORM_TYPE_RSVP,
    FORM_TYPE_TICKET_SELECTION,
    FORM_TYPE_TICKET_SALE,
} from 'common/entities/form';
import { ENTITIES_KEY_NODE, formNodeSchema } from 'common/entities/formNode';
import { ENTITIES_KEY_FORM_LOGIC_RULE } from 'common/entities/formLogicRule';
import { LOGIC_PROPERTY_VISIBLE } from 'common/entities/formLogicRule/constants';
import { getForm, getRootNodeId } from 'common/entities/form/selectors';
import { getTicketStyles } from 'common/entities/form/styleConfiguration/selectors';
import { getFormNode, getFormNodes } from 'common/entities/formNode/selectors';
import { getFormNodeLogicRules, getFormLogicRules } from 'common/entities/formLogicRule/selectors';
import { OWNER_TICKET_SALE_TYPE } from 'common/compounds/FormBuilder/constants/ownerTypes';
import { startPollingProcesses } from 'common/compounds/ProcessTracker/actions';

import { fetchApiQuestions } from './templateLibrary';
import { applyCallbackToNodeWithAdditionalGuestsSupport } from './nodeActions';

import {
    MOUNT_FORM_BUILDER,
    UNMOUNT_FORM_BUILDER,
    LOADING_BOOTSTRAP_DATA,
    LOADED_BOOTSTRAP_DATA,
    ERROR_LOADING_BOOTSTRAP_DATA,
    LOADING_FORM_DATA,
    LOADED_FORM_DATA,
    SAVING_FORM_DATA,
    SAVED_FORM_DATA,
    ERROR_SAVING_FORM_DATA,
    OPEN_EDIT_FORM_NODE_LOGIC,
    OPEN_EDIT_FORM_NODE,
    ADDING_QUESTION_TO_BANK,
    ADDED_QUESTION_TO_BANK,
    ERROR_ADDING_QUESTION_TO_BANK,
    SAVE_EDIT_FORM_NODE,
    CANCEL_EDIT_FORM_NODE_LOGIC,
    SAVE_EDIT_FORM_NODE_LOGIC,
    OVERRIDE_FORM_NODE,
    DUPLICATE_FORM_NODE,
    SET_VALIDATION_ERRORS,
    REMOVE_VALIDATION_ERRORS,
    SET_ACTIVE_SIDEBAR_TAB,
    OPEN_EDIT_FORM_HEADLINE_VIEW,
    CLOSE_SAVE_EDIT_FORM,
    SET_FREEMIUM_SELECTED_MODAL,
    CLOSE_FREEMIUM_SELECTED_MODAL,
    OPEN_EDIT_FORM_SUBMIT_BUTTON,
    SET_OPEN_STYLE_EDITOR_SECTION,
    OPEN_EDIT_FORM_TICKET_STYLING,
    SET_OPEN_TICKET_STYLE_EDITOR_SECTION,
    OVERRIDE_FORM_BUILDER_TICKET_STYLES,
    CLEAR_FORM_BUILDER_STATE,
    SET_LIVE_ERRORS,
    UPSERT_LIVE_ERRORS,
    TOGGLE_IS_PRISTINE,
    OPEN_SESSION_REGISTRATION_SETTING,
} from '../constants/actionTypes';

import { STYLE } from '../constants/sidebarTabValues';
import { MODAL_LOGIC, MODAL_STYLES } from '../constants/modals';

import {
    getFormId,
    getEditLogicFormNode,
    getBackupLogicFormNode,
    getBootstrap,
    isBootstrapLoaded,
    getEditLogicFormNodeId,
    getBackupLogicFormNodeId,
    getFormIsLoading,
    getEditFormNodeId,
    getStylesFlag,
    getLogicFlag,
    getAdditionalGuestGroupNodesLength,
    getFormLiveErrors,
} from '../selectors';

import { removeFormLogicEffect } from '../epics/formLogicEffects';
import parseNodeToBankQuestion from '../utils/parseNodeToBankQuestion';
import { transformOutputQuestionData } from '../utils/payloadTransformations';
import { validateFormNodeWithNameCode } from '../utils/formNodeValidations';
import { validateFormData, isTemporaryId } from '../utils/formValidations';
import { avoidCodeCollisions } from '../utils/nodeManagment';
import {
    EMPTY_LABEL,
    NO_OPTIONS,
    EMPTY_CODE,
    REPEATED_CODE,
    INVALID_DATES,
    INVALID_DURATION_VALUE,
    INVALID_TIMES,
} from '../constants/errorCodes';
import { NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP } from '../../../entities/formNode/nodeNames';
import { setFormLibraryError, setLibraryFormStatus } from './formLibrary';
import { DEFAULT } from '../reducers/formLibrary';

// Error messages.
const ADD_TO_BANK_SUCCESS_MESSAGE = 'Question added to your question bank!';
const ADD_TO_BANK_ERROR_MESSAGE = 'Error adding question to bank. Please try again.';
export const LOADING_FORM_DATA_ERROR_MESSAGE = 'Error loading form data. Please try again.';
const LOADING_BOOTSTRAP_DATA_ERROR_MESSAGE = 'Error loading app data. Please try again.';
const SAVING_FORM_ERROR_MESSAGE = 'Error saving form.';
const LABEL_EMPTY_ERROR_MESSAGE = "Sorry, can't save form with empty labels.";
const CODE_REPEATED_ERROR_MESSAGE =
    "Sorry, can't save the form with repeated field names. Please change your question field names before saving.";
const CODE_EMPTY_ERROR_MESSAGE = "Sorry, can't save form with empty field names.";
const NO_OPTIONS_ERROR_MESSAGE = "Sorry, can't save form with a multiple choice question that has no options.";
const FORM_TOO_LARGE_MESSAGE = "Error saving form because it's too large. Please remove some questions and try again.";
const DATES_ORDER_ERROR_MESSAGE = 'Start date must be before end date.';
const DURATION_VALUE_ERROR_MESSAGE = 'Please, select a valid duration value.';
const TIMES_ORDER_ERROR_MESAGE = 'Start time must be before end time';
const FORM_LIBRARY_NAME_ERROR = 'Form library form name already exists';

export const validationErrorMessages = {
    [EMPTY_LABEL]: LABEL_EMPTY_ERROR_MESSAGE,
    [NO_OPTIONS]: NO_OPTIONS_ERROR_MESSAGE,
    [EMPTY_CODE]: CODE_EMPTY_ERROR_MESSAGE,
    [REPEATED_CODE]: CODE_REPEATED_ERROR_MESSAGE,
    [INVALID_DATES]: DATES_ORDER_ERROR_MESSAGE,
    [INVALID_DURATION_VALUE]: DURATION_VALUE_ERROR_MESSAGE,
    [INVALID_TIMES]: TIMES_ORDER_ERROR_MESAGE,
};

const ERROR_TYPE_CUSTOM_RSVP_FIELDS_OVERFLOW = 'CustomRsvpFieldsOverflow';
const errorMessagesByType = {
    [ERROR_TYPE_CUSTOM_RSVP_FIELDS_OVERFLOW]: FORM_TOO_LARGE_MESSAGE,
};

// Will be used to map an RsvpMethod to a FormType.
const RSVP_METHOD_TO_FORM = {
    [REGISTRATION_METHOD_RSVP]: FORM_TYPE_RSVP,
    [REGISTRATION_METHOD_TICKETS]: FORM_TYPE_TICKET_SELECTION,
};

/**
 * @param  {string} type
 * @param  {Object} entities
 * @return {Object} action.
 */
const createActionUpdateEntities = (type, entities) => ({
    type,
    payload: { entities },
});
export const createActionUpdateFormBuilderEntities = (type, entities) => ({
    type,
    payload: { formBuilderEntities: entities },
});

export const createActionLoadingFormData = (formId) => ({
    type: LOADING_FORM_DATA,
    formId,
});
export const createActionLoadedFormData = (formId) => ({
    type: LOADED_FORM_DATA,
    formId,
});
export const createActionUpsertLiveErrors = (errors) => ({
    type: UPSERT_LIVE_ERRORS,
    payload: errors,
});
export const createActionSetLiveErrors = (errors) => ({
    type: SET_LIVE_ERRORS,
    payload: errors,
});
export const createActionSavingFormData = (formId) => ({
    type: SAVING_FORM_DATA,
    formId,
});
/**
 * @param {string} formId
 * @param {Object} formData
 */
export const createActionSavedFormData = (formId, formData) => ({
    type: SAVED_FORM_DATA,
    payload: {
        formBuilderEntities: formData.entities,
    },
    formId,
});
const createActionErrorSavingFormData = (formId) => ({
    type: ERROR_SAVING_FORM_DATA,
    formId,
});
const createActionOpenEditFormNode = (nodeId) => ({
    type: OPEN_EDIT_FORM_NODE,
    payload: {
        editFormNodeId: nodeId,
    },
});
const createActionOpenEditFormNodeLogic = (nodeId, backupNodeId, entities) => ({
    type: OPEN_EDIT_FORM_NODE_LOGIC,
    editNodeLogicId: nodeId,
    prevNodeId: backupNodeId,
    entities,
    payload: {
        entities,
    },
});
const createActionAddingQuestionToBank = (formNodeId) => ({
    type: ADDING_QUESTION_TO_BANK,
    formNodeId,
});
const createActionAddedQuestionToBank = (formNodeId) => ({
    type: ADDED_QUESTION_TO_BANK,
    formNodeId,
});
const createActionErrorAddingQuestionToBank = (formNodeId) => ({
    type: ERROR_ADDING_QUESTION_TO_BANK,
    formNodeId,
});
const setValidationErrors = (errors) => ({
    type: SET_VALIDATION_ERRORS,
    errors,
});

export const removeValidationErrors = () => ({
    type: REMOVE_VALIDATION_ERRORS,
});

export const resetEditingFormNode = () => ({
    type: SAVE_EDIT_FORM_NODE,
});

export const mountFormBuilder = () => ({
    type: MOUNT_FORM_BUILDER,
});
export const unmountFormBuilder = () => ({
    type: UNMOUNT_FORM_BUILDER,
});

export const setFreemiumSelectedModal = (selected) => ({
    type: SET_FREEMIUM_SELECTED_MODAL,
    payload: selected,
});

export const closeFreemiumSelectedModal = () => ({
    type: CLOSE_FREEMIUM_SELECTED_MODAL,
});

/**
 * Sets the active tab on the form builder editing sidebar.
 * @param {string} activeSidebarTab - questions, style
 * @return {Object} - action
 */
export const createSetActiveSidebarTabAction = (activeSidebarTab) => ({
    type: SET_ACTIVE_SIDEBAR_TAB,
    activeSidebarTab,
});

export const setActiveSidebarTab = (activeSidebarTab) => (dispatch, getState) => {
    const state = getState();

    if (activeSidebarTab === STYLE && !getStylesFlag(state)) {
        dispatch(setFreemiumSelectedModal(MODAL_STYLES));
        return;
    }

    dispatch(createSetActiveSidebarTabAction(activeSidebarTab));
};

/**
 * Loads form data for the form builder
 *
 * @param  {string} id - form id.
 * @param  {bool} [params.firstTimeOnly] - pass to prevent loading same form data twice.
 * @return {Promise}
 */
export const loadFormDataByFormId =
    (formId, { firstTimeOnly = false } = {}) =>
    (dispatch, getState) => {
        // Avoid fetching again if parameter passed.
        const state = getState();

        if (firstTimeOnly && getForm(state, formId) && !getFormIsLoading(state, formId)) {
            return Promise.resolve();
        }

        dispatch(createActionLoadingFormData(formId));

        return getFormBuilderApiClient()
            .loadFormDataByFormId(formId)
            .then((response) => {
                const formData = get(response, 'data.form');
                dispatch(addRawFormData(formData));
                dispatch(createActionLoadedFormData(formId));
            })
            .catch((error) => {
                dispatch(notifyError(LOADING_FORM_DATA_ERROR_MESSAGE));
                throw error;
            });
    };

export const setBootstrapData = (bootstrapData) => ({
    type: LOADED_BOOTSTRAP_DATA,
    bootstrapData,
});

/**
 * Fetches bootstrap data.
 * @return {Promise}
 */
export const loadBootstrapData = () => (dispatch, getState) => {
    // Avoid creating the fetch promise twice.
    const state = getState();
    if (isBootstrapLoaded(state)) {
        return Promise.resolve();
    }

    dispatch({ type: LOADING_BOOTSTRAP_DATA });

    // Fetch bootstrap data.
    return getFormBuilderApiClient()
        .loadBootstrapData()
        .then((response) => dispatch(setBootstrapData(response.data)))
        .catch((error) => {
            dispatch({ type: ERROR_LOADING_BOOTSTRAP_DATA });
            dispatch(notifyError(LOADING_BOOTSTRAP_DATA_ERROR_MESSAGE));
            throw error;
        });
};

export const clearFormBuilderState = () => ({
    type: CLEAR_FORM_BUILDER_STATE,
});

/**
 * Receives the response and returns a Form from the response using the
 * event rsvpMethod as a filter.
 * Forms can come as an Object already filtered by type or inside an Array where
 * the filter will be used to get the proper one.
 * This will be simplified after API improvements.
 * @param {Object} response
 * @param {string} rsvpMethod
 * @return {Object}
 */
const getFormDataFromResponse = (response, rsvpMethod, ownerType) => {
    const formType = ownerType === OWNER_TICKET_SALE_TYPE ? FORM_TYPE_TICKET_SALE : RSVP_METHOD_TO_FORM[rsvpMethod];
    return get(response, 'data.form') || get(response, 'data.forms', []).find((form) => form.type === formType);
};

/**
 * Dispatches the normalized data
 * @param {Object} formData
 */
export const setNormalizedFormData = (formData) => (dispatch) => {
    const normalizedFormData = normalizeForm(formData);
    const formDataId = `${formData.id}`;
    dispatch(createActionSavedFormData(formDataId, normalizedFormData));
    dispatch(createActionLoadedFormData(formDataId));
};

/**
 * Loads form data for the form builder
 *
 * @param  {string} id - Id of the event owning a form.
 * @param  {string} discriminator - rsvp method of the given event.
 * @return {Promise}
 */
export const loadFormDataByByOwnerAndOwnerId = (ownerType, ownerId, discriminator) => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);

    dispatch(createActionLoadingFormData(formId));

    return getFormBuilderApiClient()
        .loadFormByOwnerAndOwnerId(ownerType, ownerId, discriminator)
        .then((response) => {
            const formData = getFormDataFromResponse(response, discriminator, ownerType);

            // If no forms, throw an error.
            if (!formData) {
                getFormBuilderApiClient()
                    .createFormByOwnerAndOwnerId(ownerType, ownerId)
                    .then((resp) => {
                        const newFormData = getFormDataFromResponse(resp, discriminator, ownerType);
                        if (!isEmpty(newFormData)) {
                            dispatch(setNormalizedFormData(newFormData));
                        } else {
                            throw new Error('Error creating form');
                        }
                    })
                    .catch((error) => {
                        dispatch(notifyError(error.message));
                    });
            } else {
                dispatch(setNormalizedFormData(formData));
            }
        })
        .catch((error) => {
            dispatch(notifyError(error.message || LOADING_FORM_DATA_ERROR_MESSAGE));
        });
};

export const fetchTicketSaleForm = () => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);

    dispatch(createActionLoadingFormData(formId));

    getFormBuilderApiClient()
        .fetchTicketSaleForm()
        .then((response) => {
            const formData = get(response, 'data.form', {});
            const formDataWithId = { ...formData, id: `_${uuid()}` };
            dispatch(setNormalizedFormData(formDataWithId));
        })
        .catch((error) => {
            dispatch(notifyError(error.message || LOADING_FORM_DATA_ERROR_MESSAGE));
        });
};

export const createFormOwnershipWithTicketType = (ticketId) => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);

    getFormBuilderApiClient()
        .createFormOwnershipWithTicketType(ticketId, formId)
        .catch((error) => {
            dispatch(notifyError(error.message || LOADING_FORM_DATA_ERROR_MESSAGE));
        });
};

export const validateFormNodeErrors = (formNodeId) => (dispatch, getState) => {
    const state = getState();
    const editingFormNode = getFormNode(state, formNodeId);
    const liveErrors = getFormLiveErrors(state);

    if (editingFormNode) {
        const formNodes = getFormNodes(state, editingFormNode.formId);

        const errors = validateFormNodeWithNameCode(editingFormNode, formNodes, liveErrors);
        dispatch(setValidationErrors(errors));
        return errors;
    }

    return [];
};

export const saveEditFormNode = () => (dispatch, getState) => {
    const state = getState();

    const editingFormNodeId = getEditFormNodeId(state);
    const errors = dispatch(validateFormNodeErrors(editingFormNodeId));

    if (!errors.length) {
        dispatch(resetEditingFormNode());
    }
};

export const saveEditForm = () => ({
    type: CLOSE_SAVE_EDIT_FORM,
});

/**
 * Returns logic rule ids of rules that are targeting unexisting nodes.
 * @param {Object} state
 * @param {string} formId
 * @return {string[]}
 */
const getDetachedLogicRuleIds = (state, formId) => {
    const logicRules = getFormLogicRules(state, formId);
    const logicRulesByTarget = groupBy(logicRules, 'target');

    const detachedTargets = Object.keys(logicRulesByTarget).filter((target) => !getFormNode(state, target));

    const detachedLogicIds = detachedTargets.reduce((memo, detachedTarget) => {
        const detachedLogicRuleIds = logicRulesByTarget[detachedTarget].map((rule) => rule.id);
        return [...memo, ...detachedLogicRuleIds];
    }, []);

    return detachedLogicIds;
};

/**
 * Removes logic rules referencing nodes that don't exist.
 * @param {string} formId
 * @return {function}
 */
export const removeDetachedLogicRuleIds = (formId) => (dispatch, getState) => {
    const state = getState();
    const detachedLogicRuleIds = getDetachedLogicRuleIds(state, formId);

    if (detachedLogicRuleIds.length) {
        dispatch(removeFormLogicEffect(detachedLogicRuleIds));
    }
};

/**
 * @return {bool}
 */
export const validateFormDataForSaving = () => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);
    const formNodes = getFormNodes(state, formId);
    const liveErrors = getFormLiveErrors(state);

    const errors = validateFormData(formNodes, liveErrors);

    if (errors.length) {
        errors.forEach((error) => {
            const errorMessage = validationErrorMessages[error];
            dispatch(notifyError(errorMessage));
        });
        return false;
    }

    return true;
};

export const createFormData = (formId) => (dispatch, getState) => {
    const state = getState();

    const formIdToSave = formId || getFormId(state);

    const eventId = getEventId(state);

    formIdToSave && removeDetachedLogicRuleIds(formIdToSave)(dispatch, getState);

    const bootstrap = getBootstrap(state);
    const data = {
        form: serializeForm(formIdToSave, state.entities, bootstrap),
        eventId,
    };

    return { data, formIdToSave };
};

/**
 * Saves form data from the form builder
 *
 * @param  {string} [formId] - a form ID or the form tree to save
 * @return {Promise}
 */
export const saveFormBuilderFormData =
    (formId = null) =>
    (dispatch, getState) => {
        const formValidatesForSaving = dispatch(validateFormDataForSaving());

        // If there are errors, cancel the saving process.
        if (!formValidatesForSaving) {
            return Promise.resolve();
        }

        const { data, formIdToSave } = createFormData(formId)(dispatch, getState);

        dispatch(createActionSavingFormData(formIdToSave));

        const promise =
            formIdToSave && !isTemporaryId(formIdToSave)
                ? getFormBuilderApiClient().updateFormData(data)
                : getFormBuilderApiClient().saveNewFormData(data);

        return promise.then((response) => {
            const responseFormData = get(response, 'data.form');
            const isQueued = get(response, 'meta.queued', false);
            if (isQueued) {
                dispatch(startPollingProcesses());
            }

            dispatch(setNormalizedFormData(responseFormData));
        });
    };

export const saveFormData =
    (formId = null) =>
    (dispatch, getState) => {
        const state = getState();
        const formIdToSave = formId || getFormId(state);

        const promise = dispatch(saveFormBuilderFormData(formId));

        return promise.catch((error) => {
            const errorType = get(error, 'data.data.error_type');
            const errorMessage = errorMessagesByType[errorType] || SAVING_FORM_ERROR_MESSAGE;
            dispatch(notifyError(errorMessage));
            dispatch(createActionErrorSavingFormData(formIdToSave));
            throw error;
        });
    };

export const saveFormLibraryData =
    (formId = null) =>
    (dispatch, getState) => {
        const state = getState();
        const formIdToSave = formId || getFormId(state);

        const promise = dispatch(saveFormBuilderFormData(formId));

        return promise.catch((error) => {
            const errorMessageScaledForms = get(error, 'meta.message');
            const isFormNameError = errorMessageScaledForms === FORM_LIBRARY_NAME_ERROR;

            if (isFormNameError) {
                dispatch(setFormLibraryError(true));
                dispatch(setLibraryFormStatus(DEFAULT));
            } else {
                const errorType = get(error, 'data.data.error_type');
                const errorMessage = errorMessagesByType[errorType] || SAVING_FORM_ERROR_MESSAGE;
                dispatch(notifyError(errorMessage));
            }

            dispatch(createActionErrorSavingFormData(formIdToSave));
            throw error;
        });
    };

/**
 * Resets form data for the form builder to the current state stored in back-end.
 *
 * @param  {string} id - form ID
 * @return {Promise}
 */
export const resetFormData = loadFormDataByFormId;

/**
 * Opens a form node for editing, creating a clone whose data we can revert to on cancel
 *
 * @param  {string} formNodeId - form node ID
 */
export const openEditFormNode = (formNodeId) => (dispatch) => {
    dispatch(createActionOpenEditFormNode(formNodeId));
};

const generateEntityId = (idKey = 'formNodeBackup') => `_${idKey}_${uuid()}`;

const cloneNodeLogicRules = (formNodeLogicRules, clonedNodeId) => {
    const clonedLogicRules = formNodeLogicRules.map((logicRule) => ({
        ...logicRule,
        id: generateEntityId(),
        target: clonedNodeId,
    }));
    return keyBy(clonedLogicRules, 'id');
};

const cloneNodeEntity = (state, formNodeId, newId, parent) => ({
    ...cloneDeep(getFormNode(state, formNodeId)),
    id: newId,
    parent,
});

/**
 * Opens the logic editor modal for a form node
 *
 * @param  {string} formNodeId
 */
export const openEditFormNodeLogic = (formNodeId) => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);
    const rootNodeId = getRootNodeId(state, formId);
    const rootNode = getFormNode(state, rootNodeId);

    const backupNodeId = generateEntityId(formNodeId);
    const backupNode = cloneNodeEntity(state, formNodeId, backupNodeId);

    const formNodeLogicRules = getFormNodeLogicRules(state, formId, formNodeId);
    const backupLogicRules = cloneNodeLogicRules(formNodeLogicRules, backupNodeId);

    const entities = {
        [ENTITIES_KEY_NODE]: {
            [rootNodeId]: {
                ...rootNode,
                logic: [...rootNode.logic, ...Object.keys(backupLogicRules)],
            },
            [backupNodeId]: backupNode,
        },
        [ENTITIES_KEY_FORM_LOGIC_RULE]: backupLogicRules,
    };

    if (!getLogicFlag(state)) {
        dispatch(setFreemiumSelectedModal(MODAL_LOGIC));
        return;
    }

    dispatch(createActionOpenEditFormNodeLogic(formNodeId, backupNodeId, entities));
};

const LABEL_COPY_REGEXP = /^\s*(.*?)(?: \[copy(?:\s+(\d+))?\])?\s*$/i;

const removeLabelCopy = (label) => label.replace(LABEL_COPY_REGEXP, '$1');

const getLabelCopyNumber = (label) => parseInt(label.replace(LABEL_COPY_REGEXP, '$2') || 0, 10);

const uniqueCopyLabel = (baseLabel, count) => `${baseLabel} [copy ${count}]`;

const generateUniqueLabel = (originalLabel, parentNodeChildrenLabels, nodeId = null) => {
    const baseLabel = originalLabel ? removeLabelCopy(originalLabel) : null;

    const count = (parentNodeChildrenLabels || []).reduce((currentCount, label) => {
        if (label && removeLabelCopy(label) === baseLabel) {
            const labelCount = getLabelCopyNumber(label);
            return Math.max(currentCount, labelCount);
        }
        return currentCount;
    }, 0);
    return uniqueCopyLabel(baseLabel, count + 1);
};

/**
 * Clones a form node and attaches it to the parent.
 *
 * @param  {string} parentNodeId - parent form node ID
 * @param  {string} sourceNodeId - child form node ID
 * @param  {number} position - index to attach the node, negative to count from the bottom
 * @param  {Object} options
 * @param  {boolean} options.shouldCopyCompletely - makes a copy of label and code
 * @param  {string} options.crossGroupId
 * @param  {string} options.clonedNodeId
 */
export const duplicateFormNode =
    (
        parentNodeId,
        sourceNodeId,
        position = -1,
        { shouldCopyCompletely = false, crossGroupId = null, clonedNodeId = generateEntityId(sourceNodeId) } = {},
    ) =>
    (dispatch, getState) => {
        const state = getState();
        const formId = getFormId(state);
        const rootNodeId = getRootNodeId(state, formId);
        const rootNode = getFormNode(state, rootNodeId);
        const formNodes = getFormNodes(state, formId);

        const parentNode = getFormNode(state, parentNodeId);

        const isAdditionalGuest = get(parentNode, 'name') === NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP;
        const clonedNodeEntity = cloneNodeEntity(state, sourceNodeId, clonedNodeId, parentNodeId);

        const clonedNode = isAdditionalGuest ? clonedNodeEntity : avoidCodeCollisions(formNodes)(clonedNodeEntity);

        const formNodeLogicRules = getFormNodeLogicRules(state, formId, sourceNodeId);
        const clonedLogicRules = cloneNodeLogicRules(formNodeLogicRules, clonedNodeId);

        const parentNodeChildrenLabels = parentNode.children.map((childId) => getFormNode(state, childId).label);

        const newLabel = shouldCopyCompletely
            ? clonedNode.label
            : generateUniqueLabel(clonedNode.label, parentNodeChildrenLabels, clonedNodeId);

        const newLabelMarkup = clonedNode.labelMarkup ? clonedNode.labelMarkup.replace(clonedNode.label, newLabel) : '';

        const newCode = shouldCopyCompletely ? clonedNode.code : '';

        const newRootNode = {
            [rootNodeId]: {
                logic: [...rootNode.logic, ...Object.keys(clonedLogicRules)],
            },
        };

        const newParentNode = {
            [parentNodeId]: {
                children: [
                    ...slice(parentNode.children, 0, position),
                    clonedNodeId,
                    ...slice(parentNode.children, position),
                ],
            },
        };

        const entities = {
            [ENTITIES_KEY_NODE]: {
                ...merge(newRootNode, newParentNode),
                [clonedNodeId]: {
                    ...clonedNode,
                    crossGroupId: crossGroupId || clonedNode.crossGroupId,
                    label: newLabel,
                    labelMarkup: newLabelMarkup,
                    code: newCode,
                    children: [],
                },
            },
            [ENTITIES_KEY_FORM_LOGIC_RULE]: clonedLogicRules,
        };

        dispatch(createActionUpdateEntities(DUPLICATE_FORM_NODE, entities));
        clonedNode.children.forEach((childId, index) =>
            dispatch(
                duplicateFormNode(clonedNodeId, childId, index, {
                    shouldCopyCompletely: true,
                }),
            ),
        );

        return clonedNodeId;
    };

/**
 * Handles duplicating a form node and opens it after that.
 *
 * @param {string} parentNodeId
 * @param {string} sourceNodeId
 * @param {number} position
 */
export const duplicateAndOpenFormNode = (parentNodeId, sourceNodeId, position) => (dispatch) => {
    const clonedNodeId = generateEntityId(sourceNodeId);

    dispatch(
        duplicateFormNode(parentNodeId, sourceNodeId, position, {
            clonedNodeId,
        }),
    );
    dispatch(openEditFormNode(clonedNodeId));
};

export const duplicateFormNodeWithAdditionalGuests =
    (parentNodeId, sourceNodeId, position = -1, shouldCopyCompletely) =>
    (dispatch, getState, { getNewId = uuid } = {}) => {
        const state = getState();
        const newCrossGroupId = getNewId();
        const formId = getFormId(state);
        const additionalGuestGroupNodesLength = getAdditionalGuestGroupNodesLength(state, formId);

        applyCallbackToNodeWithAdditionalGuestsSupport(state, sourceNodeId)(
            () => dispatch(duplicateAndOpenFormNode(parentNodeId, sourceNodeId, position, { shouldCopyCompletely })),
            (node, index) => {
                const clonedNodeId = dispatch(
                    duplicateFormNode(node.parent, node.id, position, {
                        shouldCopyCompletely,
                        crossGroupId: newCrossGroupId,
                    }),
                );

                if (index === additionalGuestGroupNodesLength - 1) {
                    dispatch(openEditFormNode(clonedNodeId));
                }
            },
        );
    };

/**
 * Cancels editing the logic for a form node
 *
 * If there was a backup node, copy attributes to the edit node (effectively reverting).
 * Otherwise, delete the node, since it is a new node that we changed our mind about adding.
 */
export const cancelEditFormNodeLogic =
    () =>
    (dispatch, getState, { getNewId = uuid } = {}) => {
        const state = getState();
        const editNodeLogicId = getEditLogicFormNodeId(state);

        const backupNodeLogicId = getBackupLogicFormNodeId(state);

        const formId = getFormId(state);
        const rootNodeId = getRootNodeId(state, formId);

        const rootNode = getFormNode(state, rootNodeId);
        const backupNode = getFormNode(state, backupNodeLogicId);
        const originalNode = getFormNode(state, editNodeLogicId);

        const rootLogicRules = getFormLogicRules(state, rootNode.formId);
        const backupLogicRules = getFormNodeLogicRules(state, backupNode.formId, backupNode.id);

        const logicToAdd = rootLogicRules
            .filter((rootLogicRule) => rootLogicRule.target === backupNodeLogicId)
            .map((logicRuleToAdd) => ({
                ...logicRuleToAdd,
                target: editNodeLogicId,
                id: getNewId(),
            }));

        const rootLogicToRemove = rootLogicRules
            .filter((rootLogicRule) => rootLogicRule.target === editNodeLogicId)
            .map((logicRuleToRemove) => logicRuleToRemove.id);

        const backupLogicToRemove = backupLogicRules.map((logicRuleToRemove) => logicRuleToRemove.id);

        const logicToRemove = [...rootLogicToRemove, ...backupLogicToRemove];

        const formLogicRules = {
            formLogicRules: {
                ...logicToRemove.reduce((prev, current) => ({ ...prev, [current]: null }), {}),
                ...logicToAdd.reduce((prev, current) => ({ ...prev, [current.id]: current }), {}),
            },
        };

        const formBuilderEntities = {
            ...(logicToRemove.length > 0 || logicToAdd.length > 0 ? formLogicRules : {}),
            formNodes: {
                [backupNodeLogicId]: null,
                [editNodeLogicId]: {
                    ...backupNode,
                    id: editNodeLogicId,
                    parent: originalNode.parent,
                },
                [rootNodeId]: {
                    ...rootNode,
                    logic: xor([...rootNode.logic, ...logicToAdd.map((logic) => logic.id)], logicToRemove),
                },
            },
        };

        dispatch(createActionUpdateFormBuilderEntities(CANCEL_EDIT_FORM_NODE_LOGIC, formBuilderEntities));

        return formBuilderEntities;
    };

/**
 * Persists the logic edited in the modal for a form node.
 *
 * @param  {string} id  form node ID
 */
export const saveEditFormNodeLogic = () => (dispatch, getState) => {
    const state = getState();
    const editLogicFormNode = getEditLogicFormNode(state);
    const editLogicRules = getFormNodeLogicRules(state, editLogicFormNode.formId, editLogicFormNode.id);
    const backupLogicFormNode = getBackupLogicFormNode(state);
    const backupLogicRules = getFormNodeLogicRules(state, backupLogicFormNode.formId, backupLogicFormNode.id);

    const invalidNewRules = editLogicRules.filter(
        (rule) => rule.property === LOGIC_PROPERTY_VISIBLE && (rule.operandLeft === null || rule.operandLeft === ''),
    );

    dispatch(
        createActionUpdateFormBuilderEntities(SAVE_EDIT_FORM_NODE_LOGIC, {
            [ENTITIES_KEY_NODE]: {
                [backupLogicFormNode.id]: null,
            },
            [ENTITIES_KEY_FORM_LOGIC_RULE]: {
                ...fromPairs(backupLogicRules.map((rule) => [rule.id, null])),
                ...fromPairs(editLogicRules.map((rule) => [rule.id, rule])),
                ...fromPairs(invalidNewRules.map((rule) => [rule.id, null])),
            },
        }),
    );
};

/**
 * @param {string} nodeId
 * @param {bool} includeOther
 */
export const setIncludeOtherOption = (nodeId, includeOther) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    dispatch(
        createActionUpdateEntities(OVERRIDE_FORM_NODE, {
            [ENTITIES_KEY_NODE]: {
                [nodeId]: {
                    ...node,
                    config: {
                        ...node.config,
                        includeOther: !!includeOther,
                    },
                },
            },
        }),
    );
};

/**
 * @param {string} nodeId
 * @param {string} initialValue
 */
export const setInitialValue = (nodeId, initialValue) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    dispatch(
        createActionUpdateEntities(OVERRIDE_FORM_NODE, {
            [ENTITIES_KEY_NODE]: {
                [nodeId]: {
                    ...node,
                    config: {
                        ...node.config,
                        initialValue,
                    },
                },
            },
        }),
    );
};

/**
 * @param {string} formNodeId
 * @return {Promise}
 */
export const addQuestionToFormTemplateLibrary = (formNodeId, label) => (dispatch, getState) => {
    const state = getState();
    const bootstrap = getBootstrap(state);

    const serializedFormNode = denormalize(formNodeId, formNodeSchema, state.entities);
    const serializedWithLabel = label ? { ...serializedFormNode, label } : serializedFormNode;
    const bankQuestion = parseNodeToBankQuestion(serializedWithLabel, bootstrap);
    const bankQuestionForApi = transformOutputQuestionData(bankQuestion);

    dispatch(createActionAddingQuestionToBank(formNodeId));

    getFormBuilderApiClient()
        .addQuestionToFormTemplateLibrary(bankQuestionForApi)
        .then(({ data: rawData }) => {
            dispatch(createActionAddedQuestionToBank(formNodeId));
            dispatch(fetchApiQuestions);
            // @TODO remove duration here when we have a default duration for making success go away
            dispatch(notifySuccess(ADD_TO_BANK_SUCCESS_MESSAGE, 2000));
        })
        .catch(() => {
            dispatch(createActionErrorAddingQuestionToBank(formNodeId));
            dispatch(notifyError(ADD_TO_BANK_ERROR_MESSAGE));
        });
};

/**
 * @param {string} formId
 * @return {Object} - action
 */
export const openEditFormHeadlineView = (formId) => ({
    type: OPEN_EDIT_FORM_HEADLINE_VIEW,
    payload: {
        formId,
    },
});

/**
 * @param {string} formId
 * @return {Object} - action
 */
export const openEditFormSubmitButton = (formId) => ({
    type: OPEN_EDIT_FORM_SUBMIT_BUTTON,
    payload: {
        formId,
    },
});

export const setOpenStyleEditorSection = (openStylingSection) => ({
    type: SET_OPEN_STYLE_EDITOR_SECTION,
    openStylingSection,
});

export const setOpenFormTicketStyling = (formId, ticketPanel) => ({
    type: OPEN_EDIT_FORM_TICKET_STYLING,
    payload: {
        formId,
        ticketPanel,
    },
});

export const setOpenTicketStyleEditorSection = (openTicketStylingSection) => ({
    type: SET_OPEN_TICKET_STYLE_EDITOR_SECTION,
    openTicketStylingSection,
});

/**
 * @param {string} nodeId
 * @param {string} initialValue
 */
export const setNodeDateMax = (nodeId, dateMax) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    dispatch(
        createActionUpdateEntities(OVERRIDE_FORM_NODE, {
            [ENTITIES_KEY_NODE]: {
                [nodeId]: {
                    ...node,
                    dateMax,
                },
            },
        }),
    );
};

/**
 * @param {string} nodeId
 * @param {string} initialValue
 */
export const setNodeDateMin = (nodeId, dateMin) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    dispatch(
        createActionUpdateEntities(OVERRIDE_FORM_NODE, {
            [ENTITIES_KEY_NODE]: {
                [nodeId]: {
                    ...node,
                    dateMin,
                },
            },
        }),
    );
};

export const navigateToTicketEditor = () => (dispatch) => {
    dispatch(unmountFormBuilder());
    if (window.openEventPlanningTab) {
        // This handles old world and CMS related code when
        //  going from Formbuilder to Ticket Editor.
        window.openEventPlanningTab({
            selector: '#rsvpTixTab',
            forceRefresh: true,
        });
    } else {
        // This handles splash 4 related code when going
        // from Formbuilder to Ticket Editor using iframes.
        window.SPLASH && window.SPLASH.openTicketsTab();
    }
};

/**
 * Receives the new array of ticket styles objects and override the previous one.
 * This action is in FormBuilder because the merge pattern used in styleConfiguration
 * won't let us completetly override the array nor the objects inside it.
 * @param {string} formId
 * @param {Array} newTicketStyles
 */
export const overrideFormBuilderTicketStyles = (formId, newTicketStyles) => (dispatch, getState) => {
    const state = getState();
    const formEntities = get(state, `entities[${ENTITIES_KEY_FORM}]`, {});
    const { styleConfiguration, ...formEntitiesById } = get(formEntities, formId, {});

    dispatch(
        createActionUpdateFormBuilderEntities(OVERRIDE_FORM_BUILDER_TICKET_STYLES, {
            [ENTITIES_KEY_FORM]: {
                ...formEntities,
                [formId]: {
                    ...formEntitiesById,
                    styleConfiguration: {
                        ...styleConfiguration,
                        tickets: newTicketStyles,
                    },
                },
            },
        }),
    );
};

/**
 * Receives a category to delete from the current ticket styles array. Filter the existing
 * ones and override them using an external action that's not usin the merge pattern.
 * @param {string} formId
 * @param {string} category
 */
export const deleteTicketStyleCategory = (formId, category) => (dispatch, getState) => {
    const state = getState();
    const ticketStyles = getTicketStyles(state, formId);
    const filteredTicketStyles = ticketStyles.filter((ticketStyle) => ticketStyle.type !== category);
    dispatch(overrideFormBuilderTicketStyles(formId, filteredTicketStyles));
};

export const createActionToggleIsPristine = (isPristine) => ({ type: TOGGLE_IS_PRISTINE, payload: isPristine });

export const openEditSessionStepSetting = (formId, setting) => ({
    type: OPEN_SESSION_REGISTRATION_SETTING,
    payload: {
        formId,
        setting,
    },
});
